import { type RefObject } from "react";
import { FpPropField } from "./FpPropField";
import { generatedFpProps, type FpPropDefinition } from "./fpPropSchema";

interface FpPropertiesPanelProps {
  /** The selected block's lowercased tag name. */
  tagName: string;
  /** The selected block's positional node id. */
  nodeId: string;
  /** The block's authored value per attribute name ("" when unset). */
  values: Record<string, string>;
  /** The preview container, measured for the colour a block renders in. */
  previewRef: RefObject<HTMLDivElement>;
  /** Writes (or clears, on "") one attribute on the selected block. */
  onAttributeChange: (attribute: string, value: string) => void;
  /** Props this particular block cannot act on, left out of the panel. */
  omit?: readonly string[];
}

/**
 * The properties panel generated from the fp prop schema: the block's own
 * content props inline, then its box props. Nothing here is per-tag, so a new
 * primitive reaches the panel by adding a schema entry.
 * @param tagName - The selected block's tag name
 * @param nodeId - The selected block's node id
 * @param values - The block's authored value per attribute name
 * @param previewRef - The preview container, measured for rendered colours
 * @param onAttributeChange - Writes or clears an attribute
 * @param omit - Props this particular block cannot act on
 * @returns The generated panel, or null when the tag declares no props
 */
export function FpPropertiesPanel({
  tagName,
  nodeId,
  values,
  previewRef,
  onAttributeChange,
  omit = [],
}: FpPropertiesPanelProps) {
  const props = generatedFpProps(tagName).filter(
    (prop) => !omit.includes(prop.name),
  );
  const contentProps = props.filter((prop) => prop.group === "content");
  const containerProps = props.filter((prop) => prop.group === "container");

  if (props.length === 0) {
    return null;
  }

  const field = (prop: FpPropDefinition) => {
    const value = values[prop.name] ?? "";
    // The margin control is a four-sided diagram, so it wants the full row, and
    // it resyncs on the authored value itself: keying it on the value as well
    // would remount it on its own commit and drop the focused side.
    if (prop.control === "margin") {
      return (
        <div key={`${nodeId}:${prop.name}`} className="col-span-2">
          <FpPropField
            prop={prop}
            value={value}
            nodeId={nodeId}
            previewRef={previewRef}
            onChange={onAttributeChange}
          />
        </div>
      );
    }
    return (
      <FpPropField
        key={`${nodeId}:${prop.name}:${value}`}
        prop={prop}
        value={value}
        nodeId={nodeId}
        previewRef={previewRef}
        onChange={onAttributeChange}
      />
    );
  };

  return (
    <div className="space-y-3">
      {contentProps.length > 0 && (
        <div className="space-y-2">{contentProps.map(field)}</div>
      )}

      {containerProps.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {containerProps.map(field)}
        </div>
      )}
    </div>
  );
}
